import React from 'react';
import { Film } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { REPORT_ACCENT } from './reportTheme';
import { rangeQuery } from './reportRange';
import { useCreativePreviewUrl } from './shared/useCreativePreviewUrl';
import RangeBadge from './RangeBadge';

// CTV-445: impressions by creative. Data from GET /submissions/:id/by-creative,
// which sums selfservice_campaign_creative_daily by creative and labels each with
// the advertiser's own filename (plus the matched creative's storage path, used
// to render the actual video thumbnail). Per the product rule, this section ONLY
// renders for campaigns with more than one creative — for a single creative it's
// redundant with the campaign total, so it self-hides.

interface CreativeDatum {
  creativeId: number;
  name: string;
  storagePath: string | null;
  impressions: number;
  completes: number;
}

interface ByCreativeResponse {
  byCreative: CreativeDatum[];
}

interface ByCreativeSectionProps {
  submissionId: string;
  slug?: string;
  accentColor?: string;
  secondaryColor?: string;
  from?: string | null;
  to?: string | null;
  rangeLabel?: string | null;
}

// One row: actual video thumbnail (first frame of the uploaded creative, via a
// short-lived signed URL) + name + share bar + plays + completion rate. Falls
// back to a Film glyph when the preview can't load (no file / signed-URL miss).
const CreativeRow: React.FC<{
  row: CreativeDatum;
  slug?: string;
  accent: string;
  share: number;
  rate: number;
}> = ({ row, slug, accent, share, rate }) => {
  const { url, posterUrl } = useCreativePreviewUrl(slug, row.storagePath);
  return (
    <div className="px-5 py-3 flex items-center gap-4">
      <div className="w-28 shrink-0">
        <div className="w-full aspect-video rounded-md overflow-hidden bg-slate-900 relative">
          {posterUrl ? (
            // CTV-508: server-generated poster (a representative, non-black
            // frame picked by ffmpeg at ingest). An <img> also captures cleanly
            // into the report PDF, unlike a <video>.
            <img
              src={posterUrl}
              alt={row.name || 'Creative thumbnail'}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          ) : url ? (
            // Fallback for creatives with no poster yet: `#t=0.5` media-fragment
            // seek paints the frame ~0.5s in, so a black frame 0 isn't shown.
            <video
              src={`${url}#t=0.5`}
              muted
              preload="metadata"
              playsInline
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Film className="w-3.5 h-3.5 text-white/70" />
            </div>
          )}
        </div>
        {/* Let the name run past the thumbnail width to the right rather than
            truncating it. */}
        <div className="mt-1 text-[11px] text-slate-600 whitespace-nowrap" title={row.name}>
          {row.name}
        </div>
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2.5">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: accent }} />
        </div>
        <span className="w-24 shrink-0 text-right text-[12px] text-slate-600">
          <span className="font-medium tabular-nums">{share < 1 ? '<1%' : `${Math.round(share)}%`}</span>
          <span className="text-slate-400"> of plays</span>
        </span>
      </div>
      <div className="w-24 shrink-0 text-right text-[13px] font-semibold tabular-nums text-slate-900">
        {row.impressions.toLocaleString('en-US')}
      </div>
      <div className="w-16 shrink-0 text-right text-[12px] tabular-nums text-slate-500">{rate}%</div>
    </div>
  );
};

const ByCreativeSection: React.FC<ByCreativeSectionProps> = ({ submissionId, slug, accentColor, secondaryColor, from, to, rangeLabel }) => {
  const accent = accentColor || REPORT_ACCENT;
  const marker = secondaryColor || accent;
  const [data, setData] = React.useState<ByCreativeResponse | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    // Keep the current rows on screen (dimmed) while the new range loads.
    setLoading(true);
    apiClient
      .get<ByCreativeResponse>(`/submissions/${submissionId}/by-creative${rangeQuery({ from: from ?? null, to: to ?? null })}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData({ byCreative: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, from, to]);

  const rows = data?.byCreative ?? [];

  // Self-hide until the first load resolves and for single-creative campaigns
  // (the common case), so there's no flash of an empty section. Range changes
  // keep the prior rows (dimmed) rather than collapsing the section.
  if (data === null || rows.length <= 1) return null;

  const totalImps = rows.reduce((a, r) => a + r.impressions, 0) || 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1 gap-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
          <span className="w-0.5 h-3 rounded-full" style={{ backgroundColor: marker }} />
          By commercial
          <RangeBadge label={rangeLabel} accentColor={accent} />
        </div>
      </div>
      <div
        className={`rounded-2xl border border-slate-200/80 bg-white overflow-hidden transition-opacity duration-300 ${loading ? 'opacity-60' : 'opacity-100'}`}
      >
        <div className="px-5 py-2.5 bg-slate-50/60 border-b border-slate-200/70 text-[10px] uppercase tracking-[0.12em] text-slate-400 flex items-center gap-4">
          <span className="w-28 shrink-0">Commercial</span>
          <span className="flex-1">Share of plays</span>
          <span className="w-24 shrink-0 text-right">Plays</span>
          <span className="w-16 shrink-0 text-right">Compl.</span>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map((r) => {
            const rate = r.impressions > 0 ? Math.min(100, Math.round((r.completes / r.impressions) * 100)) : 0;
            const share = (r.impressions / totalImps) * 100;
            return <CreativeRow key={r.creativeId} row={r} slug={slug} accent={accent} share={share} rate={rate} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default ByCreativeSection;
